'use client';

import React from 'react';
import Link from 'next/link';
import { clsx } from 'clsx';
import { HalcyLogo } from '../../HalcyLogo';
import { LinkedInIcon } from '@/components/icons';
import { Button, Rail, marketingStyles, useTalkToUs } from '@/components/marketing';
import styles from './HomepageFooter.module.css';

/**
 * The useful routes, in artifact order. Relative hrefs: never hardcode the halcy.ai origin.
 * `/for` is the permanent industry hub, labelled "Healthcare" until the first
 * non-healthcare industry page ships, then "Industries" (HAL-1915).
 */
export const homepageFooterLinks = [
  { href: '/about', label: 'About Halcy' },
  { href: '/learn', label: 'Learn' },
  { href: '/research', label: 'Research' },
  { href: '/case-studies', label: 'Case studies' },
  { href: '/for', label: 'Healthcare' },
  { href: '/reputation', label: 'Reputation management' },
  { href: '/privacy', label: 'Privacy' },
  { href: '/terms', label: 'Terms' },
] as const;

export const HALCY_LINKEDIN_URL = 'https://www.linkedin.com/company/halcy';

export interface HomepageFooterProps {
  /** Override for the Talk to us action. Defaults to the shared booking-modal behaviour. */
  onTalkToUs?: () => void;
}

/**
 * Midnight footer for the HAL-1545 homepage (cc-11-sage).
 *
 * A footer, not a `Section`: the root stays a `<footer>` so it is the page's
 * contentinfo landmark, painted midnight with the shared `dark` class (which
 * also flips the focus rings) and laid out on the shared `Rail`. The Talk to
 * us pill is the shared outline `Button` on dark.
 *
 * `data-site-footer` is the marker bottom-anchored components use to observe
 * footer entry into the viewport (see MobileStickyBar); keep it. The bottom
 * row deliberately carries no rule above it – the redesign has no full-width
 * horizontal rules anywhere.
 *
 * The existing `sections/Footer.tsx` stays untouched for every other page.
 */
export function HomepageFooter({
  onTalkToUs: onTalkToUsOverride,
}: HomepageFooterProps): React.ReactElement {
  const onTalkToUs = useTalkToUs('footer', onTalkToUsOverride);
  const currentYear = new Date().getFullYear();

  return (
    <footer
      data-site-footer
      data-header-zone="dark"
      className={clsx(marketingStyles.dark, styles.footer)}
    >
      <Rail>
        <div className={styles.grid}>
          <div className={styles.brand}>
            <Link href="/" aria-label="Halcy home" className={styles.brandLink}>
              <HalcyLogo
                className={styles.logo}
                iconClassName={styles.mark}
                textClassName={styles.wordmark}
              />
            </Link>
            <p className={styles.tagline}>
              Halcy helps businesses get more customers from Google and ChatGPT.
            </p>
          </div>

          <nav className={styles.nav} aria-label="Footer navigation">
            {homepageFooterLinks.map((link) => (
              <Link key={link.href} href={link.href}>
                {link.label}
              </Link>
            ))}
          </nav>

          <div className={styles.meta}>
            <Button variant="outline" onDark aria-haspopup="dialog" onClick={onTalkToUs}>
              Talk to us
            </Button>
            <a
              href={HALCY_LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Halcy on LinkedIn"
              className={styles.social}
            >
              <LinkedInIcon className={styles.socialIcon} />
              LinkedIn
            </a>
          </div>
        </div>

        <div className={styles.bottom}>
          <span>© {currentYear} Halcy</span>
        </div>
      </Rail>
    </footer>
  );
}
